<template>
  <layout-basic>
    <page-hero
      title="The unlimited learning platform built for growing businesses."
      lead="Onboard employees. Stay compliant. Develop your people. One simple platform designed for SMEs."
    />

    <section class="demo-video-section">
      <v-container style="max-width: 960px">
        <demo-video
          wide
          video-key="home"
          title="Why growing businesses choose IMPROVE"
          subtitle="A message from founder Steve Walker."
        />
      </v-container>
    </section>

    <point-grid
      title="Why organisations choose IMPROVE"
      :points="reasons"
      :columns="2"
    />

    <!-- The testimonials Steve chose for the homepage (16 Sep 2026): one
         manufacturing, one charity, one Trilogy Active, verbatim. The unnamed
         "IMPROVE customer" quote that used to sit above them is gone at his
         request; the row moves up into its place. -->
    <quote-row :quotes="homepageQuotes" title="What our customers say" />
    <!-- Two named testimonials added at Steve's request (25 Sep 2026):
         Highland Alarms and East Midlands Chamber. A row holds three at most,
         so they sit as a second row under the first. -->
    <quote-row :quotes="homepageNamedQuotes" />

    <section class="home-ready">
      <v-container class="home-ready__inner">
        <h2 class="font-sora">Ready to see IMPROVE?</h2>
        <div class="home-ready__actions">
          <router-link to="/watch-demo" class="home-ready__btn home-ready__btn--primary">
            ▶ Watch the 4-minute demo
            <small>See the platform in action.</small>
          </router-link>
          <router-link to="/experience" class="home-ready__btn home-ready__btn--teal">
            ▶ Experience IMPROVE
            <small>Take an interactive tour and start your 14-day trial.</small>
          </router-link>
        </div>
      </v-container>
    </section>

    <standard-cta />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import QuoteRow from '@/components/marketing/QuoteRow.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import DemoVideo from '@/components/DemoVideo.vue';
import { hasMarketing } from '@/utils/consent';
import {
  MANUFACTURING_EASY_TO_ADMINISTER,
  CHARITY_ADMIN_TIME_SAVED,
  TRILOGY_EASY,
  HIGHLAND_CONSISTENCY,
  EAST_MIDLANDS_CHAMBER,
} from '@/data/testimonials';

export default {
  name: 'Homepage',
  components: {
    LayoutBasic,
    PageHero,
    PointGrid,
    QuoteRow,
    StandardCta,
    DemoVideo,
  },
  metaInfo() {
    return {
      title:
        'IMPROVE | The unlimited learning platform built for growing businesses',
      meta: [
        {
          name: 'description',
          content:
            'Onboard employees, stay compliant and develop your people with IMPROVE — one simple learning platform designed for SMEs.',
        },
        {
          property: 'og:title',
          content: 'The unlimited learning platform built for growing businesses',
        },
        {
          property: 'og:description',
          content:
            'Onboard employees. Stay compliant. Develop your people. One simple platform designed for SMEs.',
        },
        {
          property: 'og:image',
          content: 'https://www.improve.online/static/SEO.png',
        },
        { property: 'og:url', content: 'https://www.improve.online/' },
      ],
      link: [{ rel: 'canonical', href: 'https://www.improve.online/' }],
    };
  },
  data() {
    return {
      homepageQuotes: [MANUFACTURING_EASY_TO_ADMINISTER, CHARITY_ADMIN_TIME_SAVED, TRILOGY_EASY],
      homepageNamedQuotes: [HIGHLAND_CONSISTENCY, EAST_MIDLANDS_CHAMBER],
      reasons: [
        'Consistent employee onboarding',
        'Simple compliance management',
        'Develop every employee',
        'Upload your own learning',
        'Simple, transparent business pricing',
      ],
    };
  },
  mounted() {
    const referralID = window.location.search;
    if (referralID !== '') {
      const getRefID = referralID.split('?');
      if (hasMarketing()) {
        localStorage.setItem('refID', getRefID[1]);
      }
      window.location.href = 'https://www.improve.online';
    }
  },
};
</script>

<style scoped>
.home-ready {
  padding: 48px 0 24px;
  text-align: center;
}

.home-ready__inner {
  max-width: 820px;
}

.home-ready h2 {
  color: var(--brand-navy, #002458);
  font-size: 30px;
  font-weight: 700;
  margin: 0 0 24px;
}

.home-ready__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.home-ready__btn {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  text-decoration: none;
  border-radius: 12px;
  padding: 22px 20px;
  font-weight: 700;
  font-size: 18px;
  color: #fff;
}

.home-ready__btn small {
  font-weight: 500;
  font-size: 14px;
  opacity: 0.9;
}

.home-ready__btn--primary {
  background: var(--brand-navy, #002458);
}

.home-ready__btn--primary:hover {
  background: var(--brand-teal, #08a0a8);
  color: #fff;
}

.home-ready__btn--teal {
  background: var(--brand-teal, #08a0a8);
}

.home-ready__btn--teal:hover {
  background: var(--brand-teal-dark, #078f96);
  color: #fff;
}

@media screen and (max-width: 700px) {
  .home-ready__actions {
    grid-template-columns: 1fr;
  }
}
</style>
